import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useApplicationErrorHandling } from 'hooks/useApplicationErrorHandling';
import { ApplicationError } from 'types/errorHandling';

import {
  CloseConfig,
  FileInformation,
  FileReadWriteState,
  HandleClose,
  HandleSetRef,
  IsVisible,
  OnCloseClick,
  PdfTronRef,
  WebViewerInstanceExtended,
} from './types';

interface FileViewModalState {
  currentIndex?: number;
  previousIndex?: number;
  nextIndex?: number;
}

const useFileViewModal = (
  onCloseClick: OnCloseClick,
  isVisible: IsVisible,
  fileInformation: FileInformation[],
  fileId?: string,
  handleFileChange?: (index: number) => void,
  onUpdateDocument?: (file: Blob, url: string) => void
) => {
  const pdfTronRef: PdfTronRef =
    useRef<Nullable<WebViewerInstanceExtended>>(null);

  const handleAppError = useApplicationErrorHandling();

  const handleSetRef: HandleSetRef = useCallback((ref) => {
    pdfTronRef.current = ref;
  }, []);

  /**
   * Calculates which index within fileInformation corresponds to a given release ID. Returns -1 if none found.
   */
  const calculateFileInformationIndexFromProcessId = useCallback(
    (releaseId: string) => {
      for (let i = 0; i < fileInformation.length; i++) {
        if (releaseId === fileInformation[i].id) {
          return i;
        }
      }
      return undefined;
    },
    [fileInformation]
  );

  const calculateNextIndex = useCallback(
    (index?: number) => {
      if (index === undefined) {
        return undefined;
      }
      for (let i = index + 1; i < fileInformation.length; i++) {
        if (fileInformation[i].fileUrl) {
          return i;
        }
      }
      return undefined;
    },
    [fileInformation]
  );

  const calculatePreviousIndex = useCallback(
    (index?: number) => {
      if (index === undefined) {
        return undefined;
      }
      for (let i = index - 1; i >= 0; i--) {
        if (fileInformation[i].fileUrl) {
          return i;
        }
      }
      return undefined;
    },
    [fileInformation]
  );

  const [fileViewModalState, setFileViewModalState] =
    React.useState<FileViewModalState>({
      currentIndex: undefined,
      nextIndex: undefined,
      previousIndex: undefined,
    });

  useEffect(() => {
    if (fileId === undefined || fileInformation.length === 0) {
      setFileViewModalState({
        currentIndex: undefined,
        nextIndex: undefined,
        previousIndex: undefined,
      });
      return;
    }

    const indexFromInitialProcessId = fileInformation.findIndex(
      (file) => file.id === fileId
    );
    if (indexFromInitialProcessId < 0) {
      handleAppError(
        new ApplicationError(
          `file id='${fileId}' not found`,
          `file id='${fileId}' not found`
        )
      );

      setFileViewModalState({
        currentIndex: undefined,
        nextIndex: undefined,
        previousIndex: undefined,
      });

      return;
    }
    // calculateFileInformationIndexFromProcessId(initialProcessId);
    setFileViewModalState({
      currentIndex: indexFromInitialProcessId,
      nextIndex: calculateNextIndex(indexFromInitialProcessId),
      previousIndex: calculatePreviousIndex(indexFromInitialProcessId),
    });

    handleFileChange?.(indexFromInitialProcessId);
  }, [
    calculateFileInformationIndexFromProcessId,
    calculateNextIndex,
    calculatePreviousIndex,
    fileInformation,
    handleFileChange,
    handleAppError,
    fileId,
  ]);

  /**
   * Calculate the URL for the file to display by looking up what index te current process ID sits at, and extracting its file URL
   *
   * Safeguards in place:
   * (a) if currentProcessId is undefined, url is empty string
   * (b) if there are no displayed files, url is empty string
   */
  const url = useMemo(
    () =>
      fileViewModalState.currentIndex !== undefined &&
      fileInformation.length > 0
        ? fileInformation[fileViewModalState.currentIndex].fileUrl
        : '',
    [fileInformation, fileViewModalState.currentIndex]
  );

  const handleNextDocumentClick = useCallback(() => {
    if (fileViewModalState.nextIndex !== undefined && handleFileChange) {
      handleFileChange(fileViewModalState.nextIndex);
    }
    setFileViewModalState({
      currentIndex: fileViewModalState.nextIndex,
      previousIndex: fileViewModalState.currentIndex,
      nextIndex:
        fileViewModalState.nextIndex !== undefined
          ? calculateNextIndex(fileViewModalState.nextIndex)
          : undefined,
    });
  }, [
    calculateNextIndex,
    fileViewModalState.currentIndex,
    fileViewModalState.nextIndex,
    handleFileChange,
  ]);

  const updateCurrentDocument = useMemo(() => {
    if (
      !onUpdateDocument ||
      url === '' ||
      /* If there is a current file, and the file is set to readOnly */
      (fileViewModalState.currentIndex !== undefined &&
        fileInformation[fileViewModalState.currentIndex].readWriteState ===
          FileReadWriteState.ReadOnly)
    )
      return undefined;

    if (url instanceof Blob) return (file: Blob) => onUpdateDocument(file, '');

    return (file: Blob) => onUpdateDocument(file, url);
  }, [fileInformation, fileViewModalState.currentIndex, onUpdateDocument, url]);

  const handlePreviousDocumentClick = useCallback(() => {
    if (fileViewModalState.previousIndex !== undefined && handleFileChange) {
      handleFileChange(fileViewModalState.previousIndex);
    }
    setFileViewModalState({
      currentIndex: fileViewModalState.previousIndex,
      previousIndex:
        fileViewModalState.previousIndex !== undefined
          ? calculatePreviousIndex(fileViewModalState.previousIndex)
          : undefined,
      nextIndex: fileViewModalState.currentIndex,
    });
  }, [
    calculatePreviousIndex,
    fileViewModalState.currentIndex,
    fileViewModalState.previousIndex,
    handleFileChange,
  ]);

  const handleClose: HandleClose = useCallback(() => {
    if (pdfTronRef.current) pdfTronRef.current.UI.closeDocument();
    onCloseClick();
  }, [onCloseClick]);

  const closeConfig: CloseConfig = useMemo(
    () => ({
      onClick: handleClose,
      isOutsideModal: true,
    }),
    [handleClose]
  );
  useEffect(() => {
    if (isVisible && pdfTronRef.current) {
      pdfTronRef.current.loadDocument();
    }
  }, [fileInformation, isVisible]);

  return useMemo(
    () => ({
      closeConfig,
      handleSetRef,
      multipleButtonsProp: {
        prevButtonEnabled: fileViewModalState.previousIndex !== undefined,
        nextButtonEnabled: fileViewModalState.nextIndex !== undefined,
        handleNextDocumentClick,
        handlePreviousDocumentClick,
        url,
      },
      updateCurrentDocument,
    }),
    [
      closeConfig,
      fileViewModalState.nextIndex,
      fileViewModalState.previousIndex,
      handleNextDocumentClick,
      handlePreviousDocumentClick,
      handleSetRef,
      updateCurrentDocument,
      url,
    ]
  );
};

export default useFileViewModal;
